import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

const FeedbackForm = () => {
  const [formData, setFormData] = useState({
    categoria: '',
    titulo: '',
    descripcion: '',
    email: '',
    anonimo: false,
    orientacion: '',
    etapaCarrera: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  // Configuración de EmailJS (reemplazar con tus datos reales)
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  // Verificar si las variables de entorno están configuradas
  const isEmailJSConfigured = EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY;

  const categorias = [
    { value: 'bug', label: '🐛 Reportar un error', color: 'var(--error-color)' },
    { value: 'sugerencia', label: '💡 Sugerencia de mejora', color: 'var(--accent-color)' },
    { value: 'nueva-funcionalidad', label: '✨ Nueva funcionalidad', color: 'var(--success-color)' },
    { value: 'contenido', label: '📚 Error en materias/correlativas', color: 'var(--warning-color)' },
    { value: 'ux', label: '🎨 Problema de usabilidad', color: 'var(--info-color)' },
    { value: 'otro', label: '💬 Otro comentario', color: 'var(--text-secondary)' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.categoria || !formData.descripcion) {
      alert('Por favor completá al menos la categoría y descripción');
      return;
    }

    // Verificar configuración de EmailJS antes de enviar
    if (!isEmailJSConfigured) {
      // En lugar de mostrar error, redirigir a email con contenido pre-rellenado
      const emailSubject = `Feedback: ${formData.categoria} - ${formData.titulo || 'Sin título'}`;
      const emailBody = `Hola Sebastian,

Te escribo desde el Mapa de Abogacía UBA para enviarte feedback:

CATEGORÍA: ${categorias.find(c => c.value === formData.categoria)?.label || formData.categoria}
TÍTULO: ${formData.titulo || 'Sin título específico'}

DESCRIPCIÓN:
${formData.descripcion}

${formData.orientacion ? `ORIENTACIÓN: ${formData.orientacion}` : ''}
${formData.etapaCarrera ? `ETAPA DE CARRERA: ${formData.etapaCarrera}` : ''}
${!formData.anonimo && formData.email ? `MI EMAIL: ${formData.email}` : ''}

Saludos!`;

      const mailtoLink = `mailto:soto127@est.derecho.uba.ar?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.open(mailtoLink, '_blank');
      
      // Limpiar formulario
      setFormData({
        categoria: '',
        titulo: '',
        descripcion: '',
        email: '',
        anonimo: false,
        orientacion: '',
        etapaCarrera: ''
      });
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      return;
    }

    setIsSubmitting(true);
    setShowError(false);

    try {
      // Preparar datos para EmailJS
      const templateParams = {
        from_name: formData.anonimo ? 'Usuario Anónimo' : (formData.email || 'Usuario sin email'),
        user_email: formData.anonimo ? 'No proporcionado' : formData.email,
        categoria: categorias.find(c => c.value === formData.categoria)?.label || formData.categoria,
        titulo: formData.titulo || 'Sin título específico',
        descripcion: formData.descripcion,
        orientacion: formData.orientacion || 'No especificada',
        etapa_carrera: formData.etapaCarrera || 'No especificada',
        timestamp: new Date().toLocaleString('es-AR', { 
          timeZone: 'America/Argentina/Buenos_Aires',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        url_origen: window.location.href,
        user_agent: navigator.userAgent.substring(0, 100), // Primeros 100 caracteres
        tipo_dispositivo: /Mobi|Android/i.test(navigator.userAgent) ? 'Móvil' : 'Desktop'
      };

      // console.debug('Enviando feedback con EmailJS...', templateParams);

      // Verificar configuración antes de enviar
      // console.debug('Configuración EmailJS:', {
      //   SERVICE_ID: EMAILJS_SERVICE_ID,
      //   TEMPLATE_ID: EMAILJS_TEMPLATE_ID,
      //   PUBLIC_KEY: EMAILJS_PUBLIC_KEY.substring(0, 10) + '...'
      // });

      // Enviar email con EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      // console.debug('Email enviado exitosamente');
      
      setShowSuccess(true);
      setFormData({
        categoria: '',
        titulo: '',
        descripcion: '',
        email: '',
        anonimo: false,
        orientacion: '',
        etapaCarrera: ''
      });

      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      // console.error('Error enviando feedback:', error);
      // console.error('Detalles del error:', { message: error.message, status: error.status, text: error.text, fullError: error });
      
      // Mostrar mensaje de error más específico
      if (error.status === 400) {
        console.error('Error 400: Revisar configuración de EmailJS - Service ID, Template ID o Public Key incorrectos');
      }
      
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoriaColor = () => {
    const categoria = categorias.find(c => c.value === formData.categoria);
    return categoria ? categoria.color : 'var(--accent-color)';
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h3 style={{ 
          color: 'var(--accent-color)', 
          margin: '0 0 10px 0',
          fontSize: '1.3em'
        }}>
          💬 Tu Opinión Importa
        </h3>
        <p style={{ 
          color: 'var(--text-secondary)',
          fontSize: '0.9em',
          lineHeight: '1.4'
        }}>
          Ayudanos a mejorar la herramienta para todos los estudiantes de Derecho UBA
        </p>
      </div>

      {showSuccess && (
        <div style={{
          backgroundColor: 'var(--success-color)',
          color: 'white',
          padding: '12px',
          borderRadius: '6px',
          textAlign: 'center',
          marginBottom: '20px',
          animation: 'fadeIn 0.3s ease'
        }}>
          {isEmailJSConfigured ? 
            '🎉 ¡Gracias por tu feedback! Tu opinión nos ayuda a mejorar.' :
            '📧 ¡Perfecto! Se abrió tu cliente de email con el mensaje pre-rellenado. Solo tenés que enviarlo.'
          }
        </div>
      )}

      {showError && (
        <div style={{
          backgroundColor: 'var(--error-color)',
          color: 'white',
          padding: '12px',
          borderRadius: '6px',
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          ❌ Hubo un error enviando tu mensaje. Intentá de nuevo o escribinos a: soto127@est.derecho.uba.ar
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Categoría */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: 'bold',
            color: 'var(--text-primary)'
          }}>
            ¿Qué tipo de feedback querés enviar? *
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '8px'
          }}>
            {categorias.map(categoria => (
              <button
                key={categoria.value}
                type="button"
                onClick={() => handleInputChange('categoria', categoria.value)}
                style={{
                  padding: '12px',
                  border: `2px solid ${formData.categoria === categoria.value ? categoria.color : 'var(--border-color)'}`,
                  borderRadius: '6px',
                  backgroundColor: formData.categoria === categoria.value 
                    ? `${categoria.color}15` 
                    : 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
              >
                {categoria.label}
              </button>
            ))}
          </div>
        </div>

        {/* Título opcional */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '5px', 
            fontWeight: 'bold',
            color: 'var(--text-primary)'
          }}>
            Título (opcional)
          </label>
          <input
            type="text"
            value={formData.titulo}
            onChange={(e) => handleInputChange('titulo', e.target.value)}
            placeholder="Ej: Error en correlativas de Procesal Civil"
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid var(--border-color)`,
              borderRadius: '4px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: '0.9em'
            }}
          />
        </div>

        {/* Descripción */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '5px', 
            fontWeight: 'bold',
            color: 'var(--text-primary)'
          }}>
            Descripción *
          </label>
          <textarea
            value={formData.descripcion}
            onChange={(e) => handleInputChange('descripcion', e.target.value)}
            placeholder="Contanos en detalle tu sugerencia, el problema que encontraste, o la mejora que proponés..."
            rows="4"
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid var(--border-color)`,
              borderRadius: '4px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: '0.9em',
              resize: 'vertical',
              minHeight: '80px'
            }}
          />
        </div>

        {/* Información opcional del estudiante */}
        <div style={{
          backgroundColor: 'var(--bg-hover)',
          padding: '15px',
          borderRadius: '6px',
          marginBottom: '15px'
        }}>
          <h4 style={{ 
            color: 'var(--accent-color)', 
            margin: '0 0 10px 0',
            fontSize: '1em'
          }}>
            📋 Info adicional (opcional)
          </h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '5px',
                fontSize: '0.9em',
                color: 'var(--text-secondary)'
              }}>
                Orientación
              </label>
              <select
                value={formData.orientacion}
                onChange={(e) => handleInputChange('orientacion', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: `1px solid var(--border-color)`,
                  borderRadius: '4px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '0.9em'
                }}
              >
                <option value="">Seleccionar...</option>
                <option value="derecho-publico">Derecho Público</option>
                <option value="derecho-privado">Derecho Privado</option>
                <option value="derecho-penal">Derecho Penal</option>
                <option value="derecho-trabajo">Derecho del Trabajo</option>
                <option value="derecho-economico">Derecho Económico</option>
                <option value="no-decidido">Aún no decidí</option>
              </select>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '5px',
                fontSize: '0.9em',
                color: 'var(--text-secondary)'
              }}>
                ¿En qué etapa de la carrera estás?
              </label>
              <select
                value={formData.etapaCarrera}
                onChange={(e) => handleInputChange('etapaCarrera', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: `1px solid var(--border-color)`,
                  borderRadius: '4px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '0.9em'
                }}
              >
                <option value="">Seleccionar...</option>
                <option value="cbc">CBC (Ciclo Básico Común)</option>
                <option value="cpc">CPC (Ciclo Profesional Común)</option>
                <option value="cpo">CPO (Ciclo Profesional Orientado)</option>
                <option value="ultimos-puntos">Últimos puntos CPO</option>
                <option value="practico">Prácticas Profesionales</option>
                <option value="graduado">Ya me recibí</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '10px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '5px',
              fontSize: '0.9em',
              color: 'var(--text-secondary)'
            }}>
              Email (por si queremos responderte)
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="tu.email@gmail.com"
              style={{
                width: '100%',
                padding: '8px',
                border: `1px solid var(--border-color)`,
                borderRadius: '4px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '0.9em'
              }}
            />
          </div>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.9em',
            color: 'var(--text-secondary)',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={formData.anonimo}
              onChange={(e) => handleInputChange('anonimo', e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            Enviar feedback anónimo
          </label>
        </div>

        {/* Botón de envío */}
        <button
          type="submit"
          disabled={isSubmitting || !formData.categoria || !formData.descripcion}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: formData.categoria && formData.descripcion 
              ? getCategoriaColor() 
              : 'var(--text-muted)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1em',
            fontWeight: 'bold',
            cursor: formData.categoria && formData.descripcion ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s ease',
            opacity: isSubmitting ? 0.7 : 1
          }}
        >
          {isSubmitting ? 
            '📤 Enviando...' : 
            isEmailJSConfigured ? 
              '🚀 Enviar Feedback' : 
              '📧 Abrir Email para Enviar'
          }
        </button>
      </form>

      <div style={{
        marginTop: '20px',
        padding: '15px',
        backgroundColor: 'var(--bg-hover)',
        borderRadius: '6px',
        fontSize: '0.8em',
        color: 'var(--text-secondary)',
        textAlign: 'center',
        lineHeight: '1.4'
      }}>
        💌 <strong>¿Preferís escribir directamente?</strong> También podés mandarnos un mail a{' '}
        <a 
          href="mailto:soto127@est.derecho.uba.ar" 
          style={{ color: 'var(--accent-color)', textDecoration: 'none' }}
        >
          soto127@est.derecho.uba.ar
        </a>
        <br/><br/>
        📚 <strong>Sobre este proyecto:</strong> Esta herramienta fue creada por Sebastian Soto, 
        estudiante próximo a recibirse de la carrera de Derecho UBA. Es un proyecto personal 
        pensado por y para estudiantes, sin fines de lucro. Como no soy un experto programando, 
        ¡tu feedback es súper valioso para mejorar la experiencia de todos! 🎓
      </div>
    </div>
  );
};

export default FeedbackForm; 